import { isEqual } from 'lodash'
import * as React from 'react'
import { connect } from 'react-redux'
import { actions } from 'react-redux-form'

import Airport from '../../../../data/models/airport'
import {
  CountryAirports,
  getAirportNames,
  getVisibleAirports,
} from '../../../../data/models/funnelOrder/airport'
import {
  getIncoming,
  getOutgoing,
  IncomingTime,
  OutgoingTime,
} from '../../../../data/models/funnelOrder/times'
import { AppState } from '../../../../data/state'

import Content from '../../../shared/content'

import { BlockItem, BlockSelect, BlockSelectType } from 'components/BlockSelect'
import { Checkbox, CheckboxWrapper } from 'components/Checkboxes'
import { Grid, GridGap } from 'components/Grid'
import { List, ListItem } from 'components/List'
import ListHeader from 'components/ListHeader'
import { Modal, ModalFooter, ModalHeader } from 'components/Modal'
import { MultiSelectWrapper } from 'components/Select'
import { SRPRS } from 'Icon/srprs'

import { destinationEnum } from '../../../../data/models/funnelOrder/destination'
import { SiteCountry } from '../../../../data/models/locale'
import {
  ActionModalInnerComponentProps,
  withActionListModal,
} from '../../../../elements/modals/ActionListModal'
import { optionSelection } from '../../../../tracking/actions/optionSelection'

export interface AirportModel {
  airports: CountryAirports[]
  incomingTime: IncomingTime
  outgoingTime: OutgoingTime
}

interface AirportModalProps
  extends ActionModalInnerComponentProps,
    AirportModalOwnProps {
  country: SiteCountry
  destination: destinationEnum
  defaultContent: any
}

interface AirportModalOwnProps {
  airports: Airport[]
  multi?: boolean
  noTimes?: boolean
}

interface AirportModalState {
  airports: Map<SiteCountry, Airport[]>
  incoming: IncomingTime
  outgoing: OutgoingTime
}

class AirportModal extends React.Component<
  AirportModalProps,
  AirportModalState
> {
  constructor(props: AirportModalProps) {
    super(props)
    this.state = {
      airports: new Map(props.value.airports),
      outgoing: props.value.outgoingTime,
      incoming: props.value.incomingTime,
    }
  }

  public mapAirportsToOptions = (
    airports: Airport[],
    nameWithAirports: boolean = false,
  ) => {
    const values: any[] = []
    getVisibleAirports(airports, this.props.destination).map(airport => {
      const content = this.props.defaultContent.stopover
        ? this.props.defaultContent.stopover.singular
        : ''
      let label = airport.name
      if (nameWithAirports) {
        label = airport.nameWithAirport
        if (
          airport.stopover ||
          (airport.stopoverOutsideEU &&
            this.props.destination === destinationEnum.outsideEurope)
        ) {
          label += ` (${content})`
        }
      }
      values.push({ value: airport.id, label })
    })
    return values
  }

  public renderTimes() {
    return this.props.noTimes ? null : (
      <>
        <div className="margin-b-s">
          <ListHeader noMarginLeft={true} isUppercase={true}>
            <strong>
              <Content contentKey="choose-flight-time">FLIGHT TIMES</Content>
            </strong>
          </ListHeader>
        </div>
        {this.props.multi ? null : (
          <div className="margin-b-s">
            <CheckboxWrapper inputName="choose-flight-time" type="vertical">
              <Checkbox
                type="radio"
                value="false"
                checked={this.state.incoming === IncomingTime.WholeDay}
                onChange={this.handleChangeFlightOption.bind(this)}
              >
                <Content contentKey="flight-time-none" addNode>
                  Doesn't matter
                </Content>
              </Checkbox>
              <Checkbox
                type="radio"
                value="true"
                checked={this.state.incoming !== IncomingTime.WholeDay}
                onChange={this.handleChangeFlightOption.bind(this)}
              >
                <Content contentKey="flight-time-matter" addNode>
                  It matters
                </Content>
              </Checkbox>
            </CheckboxWrapper>
          </div>
        )}
        <div>
          <Grid nrCols={[1, 2]} colGap={[GridGap.l]}>
            <div>
              <BlockSelect
                value={this.state.outgoing}
                onChange={this.handleChangeOutgoing}
                nrCols={[2]}
                model="outgoing"
                headerText={
                  <>
                    <Content contentKey="outward-journey">
                      Outward journey
                    </Content>
                    {this.props.multi ? '*' : null}
                  </>
                }
                type={BlockSelectType.row}
                disabled={this.state.incoming === IncomingTime.WholeDay}
              >
                <BlockItem
                  value={OutgoingTime.Morning}
                  disabled={this.state.outgoing === OutgoingTime.WholeDay}
                  label={getOutgoing(OutgoingTime.Morning)}
                />
                <BlockItem
                  value={OutgoingTime.Afternoon}
                  disabled={this.state.outgoing === OutgoingTime.WholeDay}
                  label={getOutgoing(OutgoingTime.Afternoon)}
                />
              </BlockSelect>
            </div>
            <div>
              <BlockSelect
                value={this.state.incoming}
                onChange={this.handleChangeIncoming}
                nrCols={[2]}
                model="return"
                headerText={
                  <>
                    <Content contentKey="return-journey">
                      Return journey
                    </Content>
                    {this.props.multi ? '*' : null}
                  </>
                }
                type={BlockSelectType.row}
                disabled={this.state.incoming === IncomingTime.WholeDay}
              >
                <BlockItem
                  value={IncomingTime.Morning}
                  disabled={this.state.incoming === IncomingTime.WholeDay}
                  label={getIncoming(IncomingTime.Morning)}
                />
                <BlockItem
                  value={IncomingTime.Afternoon}
                  disabled={this.state.incoming === IncomingTime.WholeDay}
                  label={getIncoming(IncomingTime.Afternoon)}
                />
              </BlockSelect>
            </div>
          </Grid>
        </div>
        {this.props.multi ? (
          <Content contentKey="flight-times-warning">...</Content>
        ) : null}
      </>
    )
  }

  public render() {
    return (
      <Modal
        size="xl"
        animationType={'1'}
        sticky={true}
        onDismiss={this.handleSave}
        contentOverflow={this.props.noTimes}
      >
        <ModalHeader icon={SRPRS.aeroplaneColored}>
          {this.props.value.destination === destinationEnum.outsideEurope ? (
            <Content contentKey="airport-departure">Airport</Content>
          ) : (
            <Content contentKey="airport-departure-times">
              Airport &amp; Departure Times
            </Content>
          )}
        </ModalHeader>
        <div className="content">
          <List horizontal={true} borderType={'horizontal-mobile'}>
            <ListItem>
              <Content
                contentKey={`safe-airlines${this.props.multi ? '-multi' : ''}`}
              >
                Safe airlines
              </Content>
            </ListItem>
            <ListItem>
              <Content
                contentKey={`hand-baggage${this.props.multi ? '-multi' : ''}`}
              >
                Including 1 piece of hand luggage pp
              </Content>
            </ListItem>
            <ListItem>
              <Content
                contentKey={`choose-airport${this.props.multi ? '-multi' : ''}`}
              >
                Select your favourite airport
              </Content>
            </ListItem>
          </List>
          <MultiSelectWrapper
            value={this.mapAirportsToOptions(
              this.state.airports.get(this.props.country) || [],
            )}
            options={this.getOptions()}
            onChange={this.handleChangeAirport}
          />
          <br />
          {this.props.destination !== destinationEnum.outsideEurope
            ? this.renderTimes()
            : null}
        </div>
        <ModalFooter onClose={this.handleClose} onSave={this.handleSave} />
      </Modal>
    )
  }

  private getOptions = () => {
    return this.mapAirportsToOptions(this.props.airports, true)
  }

  private handleSave = () => {
    if (this.props.onSave !== undefined) {
      this.props.onSave({
        airports: Array.from(this.state.airports.entries()),
        outgoingTime: this.state.outgoing,
        incomingTime: this.state.incoming,
      })
    }
  }

  private handleClose = () => {
    if (this.props.onClose !== undefined) {
      this.props.onClose()
    }
  }

  private handleChangeAirport = (value: any[]) => {
    const airports = value
      .map(val => {
        return this.props.airports.filter(x => x.id === +val.value)[0]
      })
      .filter(val => typeof val !== 'undefined')
    const newAirports = new Map(this.state.airports)
    newAirports.set(this.props.country, airports)
    this.setState({
      airports: newAirports,
    })
  }

  private handleChangeFlightOption = (value: string) => {
    if (value === 'false') {
      this.setState({
        incoming: IncomingTime.WholeDay,
        outgoing: OutgoingTime.WholeDay,
      })
    } else {
      this.setState({
        incoming: IncomingTime.Afternoon,
        outgoing: OutgoingTime.Morning,
      })
    }
  }

  private handleChangeOutgoing = (value: string) => {
    this.setState({
      outgoing: +value,
    })
  }

  private handleChangeIncoming = (value: string) => {
    this.setState({
      incoming: +value,
    })
  }
}

const AirportNames: React.FC<{
  country: SiteCountry
  value: CountryAirports[]
}> = ({ country, value }) => (
  <React.Fragment>{getAirportNames(value, country)}</React.Fragment>
)
const ConnectedAirportNames = connect((state: AppState) => ({
  country: state.locale.country,
}))(AirportNames)

const ConnectedAirportModal = connect((state: AppState) => ({
  country: state.locale.country,
  destination: state.funnelOrder.destination,
  defaultContent: state.contentEngine.defaultContent,
}))(AirportModal)

export default connect(
  (state: AppState) => ({
    value: {
      airports: state.funnelOrder.airports,
      incomingTime: state.funnelOrder.incomingTime,
      outgoingTime: state.funnelOrder.outgoingTime,
      destination: state.funnelOrder.destination,
    },
  }),
  dispatch => ({
    onChange: (airport: AirportModel) => {
      optionSelection('airport', airport)
      dispatch(actions.change('funnelOrder.airports', airport.airports))
      dispatch(actions.change('funnelOrder.incomingTime', airport.incomingTime))
      dispatch(actions.change('funnelOrder.outgoingTime', airport.outgoingTime))
    },
  }),
  (stateProps, dispatchProps, ownProps) => ({
    ...ownProps,
    ...stateProps,
    onChange: (airport: AirportModel) => {
      if (
        stateProps.value.incomingTime !== airport.incomingTime ||
        stateProps.value.outgoingTime !== airport.outgoingTime ||
        !isEqual(stateProps.value.airports, airport.airports)
      ) {
        dispatchProps.onChange(airport)
      }
    },
  }),
)(
  withActionListModal<AirportModalOwnProps>(ConnectedAirportModal, {
    icon: SRPRS.aeroplaneColored,
    subject: (value: any) => {
      if (value.destination === destinationEnum.outsideEurope) {
        return (
          <Content addNode contentKey="airport-departure">
            Airport
          </Content>
        )
      }
      return (
        <Content addNode contentKey="airport-departure-times">
          Airport &amp; Departure Times
        </Content>
      )
    },
    getValue: (value: AirportModel) => (
      <ConnectedAirportNames value={value.airports} />
    ),
  }),
)
